import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X, Sparkles, Globe, ExternalLink, ChevronRight, Loader2 } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';
import { isNexusHostingActive } from '../lib/nexusHostingSubscription';
import { fetchHostingDomains } from '../lib/nexusCustomDomain';
import CustomDomainPanel from './CustomDomainPanel';

/**
 * AI helper that walks the ADMIN through finishing their website setup after
 * a Vercel project is connected to their business:
 *
 *   1. hosting not active  → announce the site + "Activate hosting"
 *   2. hosting active      → show the preview URL + "Add your custom domain"
 *   3. domain step         → embedded domain panel (DNS records + status)
 *
 * Opens on every app launch until setup is complete (custom domain live) or
 * the user picks "Don't show again". "Skip for now" just closes this session.
 */

const NEVER_KEY_PREFIX = 'domain_helper_never_';
/** Once per browser session — reopening after in-app navigation would be nagging. */
const SESSION_KEY_PREFIX = 'domain_helper_seen_session_';

/** Window event other surfaces (Help Centre) dispatch to open the helper manually. */
export const OPEN_DOMAIN_HELPER_EVENT = 'openDomainSetupHelper';

interface DomainSetupHelperModalProps {
  /** Suppress auto-open while another onboarding surface (setup wizard) is up. */
  suppressed?: boolean;
}

function Bubble({ text }: { text: string }) {
  return (
    <div className="flex gap-3">
      <div className="shrink-0 w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
        <Sparkles className="h-5 w-5 text-white" />
      </div>
      <div className="flex-1 rounded-2xl rounded-tl-sm bg-gray-100 px-4 py-3 text-[14px] leading-relaxed text-gray-800 whitespace-pre-wrap">
        {text}
      </div>
    </div>
  );
}

export default function DomainSetupHelperModal({ suppressed = false }: DomainSetupHelperModalProps) {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'intro' | 'domain'>('intro');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  const businessId = selectedBusiness?.id;
  const vercelProjectId = ((selectedBusiness as any)?.vercel_project_id || '').toString().trim();
  const hostingActive = isNexusHostingActive(selectedBusiness?.nexus_hosting_subscription_status);
  const siteUrl = ((selectedBusiness as any)?.nexus_website_url || '').toString().trim();

  /** Setup is "done" once the saved site URL is a real custom domain. */
  const setupComplete = !!siteUrl && !siteUrl.includes('.vercel.app');

  // Only the admin (business owner) gets the helper — matches the edge
  // function's authorization for the domain actions.
  useEffect(() => {
    let active = true;
    if (!selectedBusiness?.owner_id || !supabase) return;
    supabase.auth.getUser().then(({ data }) => {
      if (active) setIsOwner(!!data?.user?.id && data.user.id === selectedBusiness.owner_id);
    });
    return () => {
      active = false;
    };
  }, [selectedBusiness?.owner_id]);

  // Auto-open once per session when the conditions hold.
  useEffect(() => {
    if (!businessId || !isOwner || suppressed || open) return;
    if (!vercelProjectId || setupComplete) return;
    try {
      if (localStorage.getItem(`${NEVER_KEY_PREFIX}${businessId}`)) return;
      if (sessionStorage.getItem(`${SESSION_KEY_PREFIX}${businessId}`)) return;
      sessionStorage.setItem(`${SESSION_KEY_PREFIX}${businessId}`, '1');
    } catch {
      /* storage unavailable → still show */
    }
    // Small delay so it appears after the app settles, not mid-load.
    const timer = setTimeout(() => {
      setStep('intro');
      setOpen(true);
    }, 2500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId, isOwner, vercelProjectId, hostingActive, setupComplete, suppressed]);

  // Manual open (Help Centre button) — bypasses the once-per-session guard.
  useEffect(() => {
    const onOpenRequest = () => {
      setStep('intro');
      setOpen(true);
    };
    window.addEventListener(OPEN_DOMAIN_HELPER_EVENT, onOpenRequest);
    return () => window.removeEventListener(OPEN_DOMAIN_HELPER_EVENT, onOpenRequest);
  }, []);

  // Load the preview URL when open with hosting active.
  useEffect(() => {
    if (!open || !hostingActive || !businessId) return;
    let cancelled = false;
    setPreviewLoading(true);
    fetchHostingDomains(businessId)
      .then((d) => {
        if (!cancelled) setPreviewUrl(d.previewUrl);
      })
      .catch(() => {
        if (!cancelled) setPreviewUrl(siteUrl && siteUrl.includes('.vercel.app') ? siteUrl : null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, hostingActive, businessId]);

  const introText = useMemo(() => {
    if (!hostingActive) {
      return `Great news — a Nexus website has been connected to your account! 🎉\n\nThere's one step before it goes live: activate website hosting. Once hosting is active you'll get a preview link right away, and I'll help you connect your own domain.`;
    }
    return `Great news — your Nexus website is connected and hosting is active! 🎉\n\nHere's your preview link — take a look at your site right now. When you're ready, I'll walk you through connecting your own domain so customers see YOUR web address instead of the temporary one.`;
  }, [hostingActive]);

  if (!open) return null;

  const dismissForever = () => {
    try {
      if (businessId) localStorage.setItem(`${NEVER_KEY_PREFIX}${businessId}`, '1');
    } catch { /* ignore */ }
    setOpen(false);
  };

  return createPortal(
    <div className="fixed inset-0 z-[100050] bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-white w-full sm:max-w-lg max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            <Globe className="h-5 w-5 text-blue-600" />
            <h2 className="text-[16px] font-semibold text-gray-900">Your website</h2>
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {step === 'intro' ? (
            <>
              <Bubble text={introText} />

              {hostingActive && (
                <div className="ml-12 rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">Preview URL</p>
                  {previewLoading ? (
                    <div className="flex items-center gap-2 text-sm text-gray-400 py-1">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                  ) : previewUrl ? (
                    <a
                      href={previewUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 inline-flex items-center gap-1 break-all font-medium"
                    >
                      {previewUrl.replace('https://', '')}
                      <ExternalLink className="h-3.5 w-3.5 shrink-0" />
                    </a>
                  ) : (
                    <p className="text-sm text-gray-500">Preview link not available yet — check My Apps.</p>
                  )}
                </div>
              )}

              <div className="ml-12 space-y-2">
                {!hostingActive ? (
                  <button
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      navigate('/my-apps?activate_hosting=1');
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                  >
                    Activate hosting
                    <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setStep('domain')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 hover:bg-blue-700 transition-colors font-semibold text-white"
                  >
                    <Globe className="h-4 w-4" />
                    Add your custom domain
                  </button>
                )}
              </div>
            </>
          ) : (
            <>
              <Bubble
                text={`Let's connect your domain!\n\nType the domain you own below (like mybusiness.com). I'll attach it to your website and show you the exact DNS records to add at your registrar — that's the company you bought the domain from, like GoDaddy or Namecheap.\n\nAfter you add the records, hit "Check status". DNS can take a few minutes to a few hours to update — you can close this and check back anytime from My Apps.`}
              />
              {businessId && (
                <div className="ml-12">
                  <CustomDomainPanel businessId={businessId} />
                </div>
              )}
              <button
                type="button"
                onClick={() => setStep('intro')}
                className="ml-12 text-[13px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
              >
                ← Back
              </button>
            </>
          )}
        </div>

        {/* Footer: skip / never */}
        <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-[13px] font-medium text-gray-500 hover:text-gray-700 transition-colors"
          >
            Skip for now — remind me later
          </button>
          <button
            type="button"
            onClick={dismissForever}
            className="text-[13px] font-medium text-gray-400 hover:text-gray-600 transition-colors"
          >
            Don't show again
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
